import { Input } from 'src/components/widgets/input';
import { Dialog } from '../../widgets/dialog';
import { FormControl } from '../../widgets/formControl';
import { showErrorNotification } from '../../widgets/notificationToaster';
import React, { FC, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { graphql } from 'relay-runtime';
import { T } from 'src/shared/helpers';
import { useValidationContext, validateRequired, ValidationStateProvider } from 'src/shared/validation';
import { renameSalesChannelDialogMutation } from './__generated__/renameSalesChannelDialogMutation.graphql';
import { renameSalesChannelDialogQuery } from './__generated__/renameSalesChannelDialogQuery.graphql';

interface RenameSalesChannelDialogProps {
    id: string;
    onClosed: () => void;
}

export const updateNameSalesChannel = graphql`
    mutation renameSalesChannelDialogMutation($input: SalesChannelDetailInput!) {
        upsertSalesChannelDetail(input: $input) {
            id
            properties {
                name
            }
        }
    }
`;

const RenameSalesChannelDialogContent: FC<RenameSalesChannelDialogProps> = (salesChannel) => {
    const query = useLazyLoadQuery<renameSalesChannelDialogQuery>(
        graphql`
            query renameSalesChannelDialogQuery($id: ID!) {
                ecommerceSettings {
                    ecommerceName
                    remoteUrl
                }
                salesChannel(id: $id) {
                    id
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                }
                subscription {
                    areSalesChannelsEnabled
                }
            }
        `,
        { id: salesChannel.id }
    );

    const [name, setName] = useState<string>(query.salesChannel?.detail?.properties?.name || '');
    const [updateNameSalesChannelMutation, isInFightSetStatus] = useMutation<renameSalesChannelDialogMutation>(updateNameSalesChannel);
    const validationContext = useValidationContext();

    const properties = { name: name };

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        updateNameSalesChannelMutation({
            variables: {
                input: {
                    id: query.salesChannel?.detail?.id,
                    salesChannelGlobalID: salesChannel.id,
                    properties: properties
                }
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed rename sales channel', 'Admin') });
            },
            onCompleted: () => {
                salesChannel.onClosed();
            }
        });
    };

    return (
        <Dialog
            title={T._('Rename sales channel', 'Admin')}
            buttons={[
                {
                    label: T._('Confirm', 'Admin'),
                    onClick: () => {
                        handleConfirm();
                    }
                }
            ]}
        >
            <FormControl label={T._('Name', 'Admin')}>
                <Input
                    value={name || ''}
                    onChange={(e) => setName(e.currentTarget.value)}
                    validationKey={`salesChannelName`}
                    validationLabel={T._('Name', 'Admin')}
                    validate={(value) => validateRequired(value)}
                    message={T._('Name is required.', 'Admin')}
                    autoFocus
                />
            </FormControl>
        </Dialog>
    );
};

const RenameSalesChannelDialog: FC<RenameSalesChannelDialogProps> = (props) => (
    <ValidationStateProvider>
        <RenameSalesChannelDialogContent {...props} />
    </ValidationStateProvider>
);

export default RenameSalesChannelDialog;
